import { QueryClient } from '@tanstack/react-query'

/** How many times a genuinely transient failure is retried. */
const MAX_RETRIES = 2

/**
 * Whether a failed query is worth trying again.
 *
 * A 4xx is the server's considered answer, not a blip: a deleted event stays
 * 404, a private one stays 403, and an expired session stays 401 (which `req`
 * already handles itself — it refreshes the token and replays the request, or
 * logs the user out). Retrying those only delays the error screen by the full
 * backoff, roughly three seconds, while the outcome cannot change.
 *
 * 5xx, timeouts and transport failures keep the retries — those genuinely do
 * succeed on a second attempt.
 *
 * `req` sets `status` and `statusCode` in separate places, and either can be
 * missing (`status` is only attached when the response carried a url, and
 * `statusCode` is skipped if the error body fails to parse), so both are read
 * before deciding. An error with no recognizable status is treated as
 * transient and retried — the safer default when the cause is unknown.
 */
export const shouldRetry = (failureCount, error) => {
  const status = error?.status ?? error?.statusCode

  if (typeof status === 'number' && status >= 400 && status < 500) {
    return false
  }

  return failureCount < MAX_RETRIES
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      staleTime: 1000 * 60, // Data is fresh for 1 minute
      cacheTime: 1000 * 60 * 10, // Cache persists for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when app comes to foreground (can enable if needed)
      refetchOnReconnect: true, // Refetch when internet reconnects
    },
    mutations: {
      retry: 1,
    },
  },
})
